import React from "react";
import { Container } from "react-bootstrap";
import { useParams, Navigate } from "react-router-dom";
import Particle from "./Particle";

const legalStyle = {
  padding: "50px 20px",
  textAlign: "left",
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  borderRadius: "10px",
  marginTop: "120px",
  color: "#333",
};

const titleStyle = { textAlign: "center", marginBottom: "30px", color: "#2c3e50" };

function HangmanGoPrivacyEN() {
  return (
    <>
      <h1 style={titleStyle}>Privacy Policy for Hangman Go</h1>

      <p><strong>Effective Date: October 2025</strong></p>

      <h3>1) Scope</h3>
      <p>
        This Privacy Policy explains how Hangman Go ("App", "we", "us") processes information when you use the App.
        The App is designed for general audiences and can be used without creating an account.
      </p>

      <h3>2) Information We Process</h3>
      <h4>A. Information You Provide</h4>
      <ul>
        <li>No account or personal registration is required to play.</li>
        <li>In-App purchase actions are handled by Apple App Store or Google Play; we do not receive your full payment details.</li>
      </ul>

      <h4>B. Automatically Processed Information</h4>
      <ul>
        <li><strong>Local game data</strong>: progress, settings, language, inventory (stored on your device).</li>
        <li><strong>Purchase status & entitlements</strong>: limited purchase receipts / entitlement status (via store and/or RevenueCat) to unlock paid content.</li>
        <li><strong>Device & usage data</strong>: basic diagnostics, app version, performance data (used to improve stability and fix bugs).</li>
        <li><strong>Advertising identifiers</strong>: Apple IDFA / Google AAID and related signals processed by ad networks (e.g., Google AdMob) for ad delivery and, where permitted, personalization/frequency capping/fraud prevention.</li>
      </ul>

      <h3>3) Purposes of Processing</h3>
      <ul>
        <li>Provide core gameplay, save local progress, and restore purchases/entitlements.</li>
        <li>Process payments and subscriptions through platform stores.</li>
        <li>Show ads (including rewarded ads where available) and measure their performance.</li>
        <li>Maintain security, prevent fraud/abuse, and improve App performance.</li>
      </ul>

      <h3>4) Data Storage & Retention</h3>
      <ul>
        <li>Game progress is stored locally on your device. Deleting the App removes local data.</li>
        <li>Store/RevenueCat purchase records and limited analytics/diagnostic data may be retained as required for legitimate purposes (e.g., billing, compliance, debugging) and then deleted or anonymized.</li>
      </ul>

      <h3>5) Third-Party Services</h3>
      <p>We integrate third parties to operate the App. Their processing is governed by their own terms/policies:</p>
      <ul>
        <li><strong>Apple App Store / Google Play Billing</strong>: purchases, subscriptions, refunds, price changes.</li>
        <li><strong>RevenueCat</strong>: subscription & entitlement management (verifies active purchases to unlock content).</li>
        <li><strong>Google AdMob</strong>: ad serving, measurement, fraud prevention; may use device identifiers and related signals.</li>
      </ul>

      <h3>6) Children's Privacy</h3>
      <ul>
        <li>The App is family-friendly; we do not knowingly collect personal data from children.</li>
        <li>Where required by law or platform policy, personalized ads are limited or disabled for child users.</li>
        <li>Parents/guardians should supervise use and can manage device privacy/ad settings.</li>
      </ul>

      <h3>7) Your Choices</h3>
      <ul>
        <li><strong>Delete local data</strong>: uninstall the App.</li>
        <li><strong>Ads preferences</strong>: reset/limit Ad IDFA/AAID, "Limit Ad Tracking" / "Opt-out of Ads Personalization" in device settings.</li>
        <li><strong>Consent (EEA/UK where applicable)</strong>: you may be shown a consent prompt for ads; you can revisit choices in the App (if provided) or device settings.</li>
      </ul>

      <h3>8) Legal Bases & Your Rights</h3>
      <p>
        Where the GDPR applies, we rely on: performance of a contract (providing the App), legitimate interests
        (fraud prevention, analytics, service improvement), and consent (personalized ads where required).
        Under KVKK and/or GDPR (if applicable), you may have rights to access, correct, delete, restrict, object to processing,
        and data portability. You can exercise rights via the contact below; for purchases/refunds manage via the store.
      </p>

      <h3>9) Data Sharing</h3>
      <ul>
        <li>We do not sell your personal data.</li>
        <li>We share data only with service providers listed above, when required by law, or to protect our rights/users.</li>
      </ul>

      <h3>10) International Transfers</h3>
      <p>
        Third-party processors (e.g., AdMob, RevenueCat, Apple/Google) may process data on servers outside your country.
        Where required, safeguards (such as standard contractual clauses) may apply under their policies.
      </p>

      <h3>11) Security</h3>
      <p>
        We use reasonable technical and organizational measures appropriate to the nature of the data and our App.
        No method of transmission or storage is 100% secure.
      </p>

      <h3>12) Updates to This Policy</h3>
      <ul>
        <li>We may update this Policy from time to time to reflect changes in features or law.</li>
        <li>Material changes will be communicated in-App and/or via the store listing. Continued use means you accept the updated Policy.</li>
      </ul>

      <h3>13) Contact</h3>
      <p>For privacy questions or to exercise your rights, contact us:</p>
      <ul>
        <li>Email: <a href="mailto:sozdemircim@gmail.com">sozdemircim@gmail.com</a></li>
        <li>Website: <a href="https://www.suleymanozdemirci.com/project" target="_blank" rel="noreferrer">https://www.suleymanozdemirci.com/project</a></li>
        <li>We aim to respond within 7 business days.</li>
      </ul>

      <h3>14) Compliance Statement</h3>
      <ul>
        <li>Designed to align with Türkiye's KVKK.</li>
        <li>Intended to meet Google Play / Apple App Store disclosure expectations.</li>
        <li>Where applicable, aligned with GDPR/EEA children's privacy and advertising requirements.</li>
      </ul>

      <hr style={{ margin: "30px 0" }} />
      <p><strong>Last Updated: October 2025</strong></p>
    </>
  );
}

// ── Hangman Go — Turkish ──
function HangmanGoPrivacyTR() {
  return (
    <>
      <h1 style={titleStyle}>Hangman Go Gizlilik Politikası</h1>

      <p><strong>Yürürlük Tarihi: Ekim 2025</strong></p>

      <h3>1) Kapsam</h3>
      <p>
        Bu Gizlilik Politikası, Hangman Go ("Uygulama", "biz") uygulamasını kullandığınızda bilgileri nasıl işlediğimizi açıklar.
        Uygulama genel kitlelere yöneliktir ve hesap oluşturmadan kullanılabilir.
      </p>

      <h3>2) İşlediğimiz Bilgiler</h3>
      <h4>A. Sağladığınız Bilgiler</h4>
      <ul>
        <li>Oynamak için hesap veya kişisel kayıt gerekmez.</li>
        <li>Uygulama içi satın alma işlemleri Apple App Store veya Google Play tarafından yürütülür; tam ödeme bilgilerinizi almayız.</li>
      </ul>

      <h4>B. Otomatik Olarak İşlenen Bilgiler</h4>
      <ul>
        <li><strong>Yerel oyun verileri</strong>: ilerleme, ayarlar, dil, envanter (cihazınızda saklanır).</li>
        <li><strong>Satın alma durumu ve haklar</strong>: ücretli içeriği açmak için sınırlı satın alma makbuzları / hak durumu (mağaza ve/veya RevenueCat aracılığıyla).</li>
        <li><strong>Cihaz ve kullanım verileri</strong>: temel tanılama, uygulama sürümü, performans verileri (kararlılığı iyileştirmek ve hataları gidermek için kullanılır).</li>
        <li><strong>Reklam tanımlayıcıları</strong>: reklam ağları (ör. Google AdMob) tarafından reklam sunumu ve izin verildiği yerlerde kişiselleştirme/sıklık sınırlama/dolandırıcılık önleme için işlenen Apple IDFA / Google AAID ve ilgili sinyaller.</li>
      </ul>

      <h3>3) İşleme Amaçları</h3>
      <ul>
        <li>Temel oyun deneyimini sağlamak, yerel ilerlemeyi kaydetmek ve satın alımları/hakları geri yüklemek.</li>
        <li>Mağaza platformları aracılığıyla ödemeleri ve abonelikleri işlemek.</li>
        <li>Reklam göstermek (varsa ödüllü reklamlar dahil) ve performanslarını ölçmek.</li>
        <li>Güvenliği sağlamak, dolandırıcılığı/kötüye kullanımı önlemek ve Uygulama performansını iyileştirmek.</li>
      </ul>

      <h3>4) Veri Depolama ve Saklama</h3>
      <ul>
        <li>Oyun ilerlemesi cihazınızda yerel olarak saklanır. Uygulamayı silmek yerel verileri kaldırır.</li>
        <li>Mağaza/RevenueCat satın alma kayıtları ve sınırlı analiz/tanılama verileri meşru amaçlar (ör. faturalandırma, uyumluluk, hata ayıklama) için gerektiği kadar saklanabilir ve ardından silinir veya anonimleştirilir.</li>
      </ul>

      <h3>5) Üçüncü Taraf Hizmetleri</h3>
      <p>Uygulamayı çalıştırmak için üçüncü taraflarla entegre oluyoruz. İşlemeleri kendi şartlarına/politikalarına tabidir:</p>
      <ul>
        <li><strong>Apple App Store / Google Play Faturalandırma</strong>: satın alımlar, abonelikler, geri ödemeler, fiyat değişiklikleri.</li>
        <li><strong>RevenueCat</strong>: abonelik ve hak yönetimi (içeriği açmak için aktif satın alımları doğrular).</li>
        <li><strong>Google AdMob</strong>: reklam sunumu, ölçüm, dolandırıcılık önleme; cihaz tanımlayıcılarını ve ilgili sinyalleri kullanabilir.</li>
      </ul>

      <h3>6) Çocukların Gizliliği</h3>
      <ul>
        <li>Uygulama aile dostudur; çocuklardan bilerek kişisel veri toplamayız.</li>
        <li>Yasa veya platform politikasının gerektirdiği yerlerde, çocuk kullanıcılar için kişiselleştirilmiş reklamlar sınırlandırılır veya devre dışı bırakılır.</li>
        <li>Ebeveynler/veliler kullanımı denetlemeli ve cihazın gizlilik/reklam ayarlarını yönetebilir.</li>
      </ul>

      <h3>7) Tercihleriniz</h3>
      <ul>
        <li><strong>Yerel verileri silme</strong>: Uygulamayı kaldırın.</li>
        <li><strong>Reklam tercihleri</strong>: Reklam IDFA/AAID'yi sıfırlayın/sınırlayın, cihaz ayarlarında "Reklam İzlemeyi Sınırla" / "Reklam Kişiselleştirmeyi Devre Dışı Bırak".</li>
        <li><strong>Onay (geçerli olduğunda AEA/Birleşik Krallık)</strong>: reklamlar için size bir onay istemi gösterilebilir; tercihlerinizi Uygulamada (varsa) veya cihaz ayarlarında yeniden gözden geçirebilirsiniz.</li>
      </ul>

      <h3>8) Yasal Dayanaklar ve Haklarınız</h3>
      <p>
        GDPR'nin geçerli olduğu durumlarda şunlara dayanırız: bir sözleşmenin ifası (Uygulamanın sağlanması), meşru menfaatler
        (dolandırıcılık önleme, analiz, hizmet iyileştirme) ve onay (gerektiğinde kişiselleştirilmiş reklamlar).
        KVKK ve/veya GDPR kapsamında (geçerliyse) erişim, düzeltme, silme, kısıtlama, işlemeye itiraz ve veri taşınabilirliği haklarına sahip olabilirsiniz.
        Haklarınızı aşağıdaki iletişim yoluyla kullanabilirsiniz; satın alımlar/geri ödemeler için mağaza üzerinden yönetin.
      </p>

      <h3>9) Veri Paylaşımı</h3>
      <ul>
        <li>Kişisel verilerinizi satmıyoruz.</li>
        <li>Verileri yalnızca yukarıda listelenen hizmet sağlayıcılarla, yasaların gerektirdiği durumlarda veya haklarımızı/kullanıcılarımızı korumak için paylaşırız.</li>
      </ul>

      <h3>10) Uluslararası Aktarımlar</h3>
      <p>
        Üçüncü taraf işleyiciler (ör. AdMob, RevenueCat, Apple/Google) verileri ülkenizin dışındaki sunucularda işleyebilir.
        Gerektiğinde, kendi politikaları kapsamında güvenceler (standart sözleşme hükümleri gibi) uygulanabilir.
      </p>

      <h3>11) Güvenlik</h3>
      <p>
        Verinin niteliğine ve Uygulamamıza uygun makul teknik ve organizasyonel önlemler kullanırız.
        Hiçbir iletim veya depolama yöntemi %100 güvenli değildir.
      </p>

      <h3>12) Bu Politikadaki Güncellemeler</h3>
      <ul>
        <li>Özelliklerdeki veya yasalardaki değişiklikleri yansıtmak için bu Politikayı zaman zaman güncelleyebiliriz.</li>
        <li>Önemli değişiklikler Uygulama içinde ve/veya mağaza listesinde bildirilecektir. Kullanmaya devam etmeniz, güncellenmiş Politikayı kabul ettiğiniz anlamına gelir.</li>
      </ul>

      <h3>13) İletişim</h3>
      <p>Gizlilik sorularınız için veya haklarınızı kullanmak için bizimle iletişime geçin:</p>
      <ul>
        <li>E-posta: <a href="mailto:sozdemircim@gmail.com">sozdemircim@gmail.com</a></li>
        <li>Web sitesi: <a href="https://www.suleymanozdemirci.com/project" target="_blank" rel="noreferrer">https://www.suleymanozdemirci.com/project</a></li>
        <li>7 iş günü içinde yanıt vermeyi hedefliyoruz.</li>
      </ul>

      <h3>14) Uyumluluk Beyanı</h3>
      <ul>
        <li>Türkiye KVKK ile uyumlu olacak şekilde tasarlanmıştır.</li>
        <li>Google Play / Apple App Store açıklama beklentilerini karşılamayı amaçlar.</li>
        <li>Geçerli olduğunda GDPR/AEA çocuk gizliliği ve reklam gereklilikleriyle uyumludur.</li>
      </ul>

      <hr style={{ margin: "30px 0" }} />
      <p><strong>Son Güncelleme: Ekim 2025</strong></p>
    </>
  );
}

// ── Hangman Go — German ──
function HangmanGoPrivacyDE() {
  return (
    <>
      <h1 style={titleStyle}>Datenschutzrichtlinie für Hangman Go</h1>

      <p><strong>Gültig ab: Oktober 2025</strong></p>

      <h3>1) Geltungsbereich</h3>
      <p>
        Diese Datenschutzrichtlinie erläutert, wie Hangman Go ("App", "wir", "uns") Informationen verarbeitet, wenn Sie die App nutzen.
        Die App richtet sich an ein allgemeines Publikum und kann ohne Erstellung eines Kontos genutzt werden.
      </p>

      <h3>2) Informationen, die wir verarbeiten</h3>
      <h4>A. Von Ihnen bereitgestellte Informationen</h4>
      <ul>
        <li>Zum Spielen ist kein Konto oder eine persönliche Registrierung erforderlich.</li>
        <li>In-App-Käufe werden vom Apple App Store oder Google Play abgewickelt; wir erhalten Ihre vollständigen Zahlungsdaten nicht.</li>
      </ul>

      <h4>B. Automatisch verarbeitete Informationen</h4>
      <ul>
        <li><strong>Lokale Spieldaten</strong>: Fortschritt, Einstellungen, Sprache, Inventar (auf Ihrem Gerät gespeichert).</li>
        <li><strong>Kaufstatus &amp; Berechtigungen</strong>: begrenzte Kaufbelege / Berechtigungsstatus (über Store und/oder RevenueCat) zum Freischalten kostenpflichtiger Inhalte.</li>
        <li><strong>Geräte- &amp; Nutzungsdaten</strong>: grundlegende Diagnose, App-Version, Leistungsdaten (zur Verbesserung der Stabilität und Fehlerbehebung).</li>
        <li><strong>Werbe-IDs</strong>: Apple IDFA / Google AAID und zugehörige Signale, die von Werbenetzwerken (z. B. Google AdMob) zur Anzeigenschaltung und, wo zulässig, zur Personalisierung/Frequenzbegrenzung/Betrugsprävention verarbeitet werden.</li>
      </ul>

      <h3>3) Zwecke der Verarbeitung</h3>
      <ul>
        <li>Bereitstellung des Kernspiels, Speichern des lokalen Fortschritts und Wiederherstellen von Käufen/Berechtigungen.</li>
        <li>Abwicklung von Zahlungen und Abonnements über die Plattform-Stores.</li>
        <li>Anzeigen von Werbung (einschließlich belohnter Anzeigen, sofern verfügbar) und Messung ihrer Leistung.</li>
        <li>Aufrechterhaltung der Sicherheit, Verhinderung von Betrug/Missbrauch und Verbesserung der App-Leistung.</li>
      </ul>

      <h3>4) Datenspeicherung &amp; Aufbewahrung</h3>
      <ul>
        <li>Der Spielfortschritt wird lokal auf Ihrem Gerät gespeichert. Das Löschen der App entfernt lokale Daten.</li>
        <li>Store-/RevenueCat-Kaufaufzeichnungen und begrenzte Analyse-/Diagnosedaten können so lange aufbewahrt werden, wie es für legitime Zwecke (z. B. Abrechnung, Compliance, Debugging) erforderlich ist, und werden dann gelöscht oder anonymisiert.</li>
      </ul>

      <h3>5) Drittanbieterdienste</h3>
      <p>Wir binden Dritte ein, um die App zu betreiben. Ihre Verarbeitung unterliegt ihren eigenen Bedingungen/Richtlinien:</p>
      <ul>
        <li><strong>Apple App Store / Google Play Billing</strong>: Käufe, Abonnements, Rückerstattungen, Preisänderungen.</li>
        <li><strong>RevenueCat</strong>: Abonnement- &amp; Berechtigungsverwaltung (überprüft aktive Käufe zum Freischalten von Inhalten).</li>
        <li><strong>Google AdMob</strong>: Anzeigenbereitstellung, Messung, Betrugsprävention; kann Gerätekennungen und zugehörige Signale verwenden.</li>
      </ul>

      <h3>6) Datenschutz für Kinder</h3>
      <ul>
        <li>Die App ist familienfreundlich; wir erheben wissentlich keine personenbezogenen Daten von Kindern.</li>
        <li>Wo gesetzlich oder durch Plattformrichtlinien vorgeschrieben, wird personalisierte Werbung für Kinder eingeschränkt oder deaktiviert.</li>
        <li>Eltern/Erziehungsberechtigte sollten die Nutzung beaufsichtigen und können die Datenschutz-/Werbeeinstellungen des Geräts verwalten.</li>
      </ul>

      <h3>7) Ihre Wahlmöglichkeiten</h3>
      <ul>
        <li><strong>Lokale Daten löschen</strong>: Deinstallieren Sie die App.</li>
        <li><strong>Werbeeinstellungen</strong>: Setzen Sie die Werbe-IDFA/AAID zurück bzw. begrenzen Sie sie, "Ad-Tracking beschränken" / "Personalisierung von Werbung deaktivieren" in den Geräteeinstellungen.</li>
        <li><strong>Einwilligung (EWR/UK, wo zutreffend)</strong>: Ihnen wird möglicherweise eine Einwilligungsaufforderung für Werbung angezeigt; Sie können Ihre Auswahl in der App (falls vorhanden) oder in den Geräteeinstellungen erneut treffen.</li>
      </ul>

      <h3>8) Rechtsgrundlagen &amp; Ihre Rechte</h3>
      <p>
        Soweit die DSGVO Anwendung findet, stützen wir uns auf: Vertragserfüllung (Bereitstellung der App), berechtigte Interessen
        (Betrugsprävention, Analyse, Serviceverbesserung) und Einwilligung (personalisierte Werbung, wo erforderlich).
        Nach KVKK und/oder DSGVO (falls zutreffend) haben Sie möglicherweise Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung,
        Widerspruch gegen die Verarbeitung und Datenübertragbarkeit. Sie können Ihre Rechte über den unten stehenden Kontakt ausüben;
        für Käufe/Rückerstattungen verwalten Sie diese über den Store.
      </p>

      <h3>9) Datenweitergabe</h3>
      <ul>
        <li>Wir verkaufen Ihre personenbezogenen Daten nicht.</li>
        <li>Wir geben Daten nur an die oben genannten Dienstleister weiter, wenn dies gesetzlich vorgeschrieben ist oder zum Schutz unserer Rechte/Nutzer.</li>
      </ul>

      <h3>10) Internationale Übermittlungen</h3>
      <p>
        Drittanbieter-Verarbeiter (z. B. AdMob, RevenueCat, Apple/Google) können Daten auf Servern außerhalb Ihres Landes verarbeiten.
        Wo erforderlich, können Schutzmaßnahmen (wie Standardvertragsklauseln) gemäß ihren Richtlinien gelten.
      </p>

      <h3>11) Sicherheit</h3>
      <p>
        Wir verwenden angemessene technische und organisatorische Maßnahmen, die der Art der Daten und unserer App entsprechen.
        Keine Übertragungs- oder Speichermethode ist zu 100 % sicher.
      </p>

      <h3>12) Aktualisierungen dieser Richtlinie</h3>
      <ul>
        <li>Wir können diese Richtlinie von Zeit zu Zeit aktualisieren, um Änderungen bei Funktionen oder Gesetzen widerzuspiegeln.</li>
        <li>Wesentliche Änderungen werden in der App und/oder über den Store-Eintrag mitgeteilt. Die fortgesetzte Nutzung bedeutet, dass Sie die aktualisierte Richtlinie akzeptieren.</li>
      </ul>

      <h3>13) Kontakt</h3>
      <p>Bei Datenschutzfragen oder zur Ausübung Ihrer Rechte kontaktieren Sie uns:</p>
      <ul>
        <li>E-Mail: <a href="mailto:sozdemircim@gmail.com">sozdemircim@gmail.com</a></li>
        <li>Website: <a href="https://www.suleymanozdemirci.com/project" target="_blank" rel="noreferrer">https://www.suleymanozdemirci.com/project</a></li>
        <li>Wir bemühen uns, innerhalb von 7 Werktagen zu antworten.</li>
      </ul>

      <h3>14) Compliance-Erklärung</h3>
      <ul>
        <li>Entwickelt, um mit der türkischen KVKK übereinzustimmen.</li>
        <li>Soll die Offenlegungserwartungen von Google Play / Apple App Store erfüllen.</li>
        <li>Wo zutreffend, abgestimmt auf die Anforderungen der DSGVO/des EWR zum Datenschutz von Kindern und zur Werbung.</li>
      </ul>

      <hr style={{ margin: "30px 0" }} />
      <p><strong>Zuletzt aktualisiert: Oktober 2025</strong></p>
    </>
  );
}

// ── Hangman Go — Spanish ──
function HangmanGoPrivacyES() {
  return (
    <>
      <h1 style={titleStyle}>Política de Privacidad de Hangman Go</h1>

      <p><strong>Fecha de entrada en vigor: Octubre de 2025</strong></p>

      <h3>1) Alcance</h3>
      <p>
        Esta Política de Privacidad explica cómo Hangman Go ("App", "nosotros") procesa la información cuando usas la App.
        La App está diseñada para el público general y se puede usar sin crear una cuenta.
      </p>

      <h3>2) Información que Procesamos</h3>
      <h4>A. Información que proporcionas</h4>
      <ul>
        <li>No se requiere cuenta ni registro personal para jugar.</li>
        <li>Las acciones de compra dentro de la app son gestionadas por Apple App Store o Google Play; no recibimos los datos completos de tu pago.</li>
      </ul>

      <h4>B. Información procesada automáticamente</h4>
      <ul>
        <li><strong>Datos locales del juego</strong>: progreso, ajustes, idioma, inventario (almacenados en tu dispositivo).</li>
        <li><strong>Estado de compra y derechos</strong>: recibos de compra / estado de derechos limitados (a través de la tienda y/o RevenueCat) para desbloquear contenido de pago.</li>
        <li><strong>Datos del dispositivo y de uso</strong>: diagnósticos básicos, versión de la app, datos de rendimiento (usados para mejorar la estabilidad y corregir errores).</li>
        <li><strong>Identificadores publicitarios</strong>: Apple IDFA / Google AAID y señales relacionadas procesadas por las redes publicitarias (p. ej., Google AdMob) para la entrega de anuncios y, cuando se permita, personalización/limitación de frecuencia/prevención de fraude.</li>
      </ul>

      <h3>3) Fines del Procesamiento</h3>
      <ul>
        <li>Proporcionar la jugabilidad principal, guardar el progreso local y restaurar compras/derechos.</li>
        <li>Procesar pagos y suscripciones a través de las tiendas de las plataformas.</li>
        <li>Mostrar anuncios (incluidos los anuncios recompensados cuando estén disponibles) y medir su rendimiento.</li>
        <li>Mantener la seguridad, prevenir el fraude/abuso y mejorar el rendimiento de la App.</li>
      </ul>

      <h3>4) Almacenamiento y Conservación de Datos</h3>
      <ul>
        <li>El progreso del juego se almacena localmente en tu dispositivo. Eliminar la App borra los datos locales.</li>
        <li>Los registros de compra de la tienda/RevenueCat y datos limitados de análisis/diagnóstico pueden conservarse según sea necesario para fines legítimos (p. ej., facturación, cumplimiento, depuración) y luego eliminarse o anonimizarse.</li>
      </ul>

      <h3>5) Servicios de Terceros</h3>
      <p>Integramos terceros para operar la App. Su procesamiento se rige por sus propios términos/políticas:</p>
      <ul>
        <li><strong>Apple App Store / Facturación de Google Play</strong>: compras, suscripciones, reembolsos, cambios de precio.</li>
        <li><strong>RevenueCat</strong>: gestión de suscripciones y derechos (verifica las compras activas para desbloquear contenido).</li>
        <li><strong>Google AdMob</strong>: publicación de anuncios, medición, prevención de fraude; puede usar identificadores del dispositivo y señales relacionadas.</li>
      </ul>

      <h3>6) Privacidad de los Niños</h3>
      <ul>
        <li>La App es apta para toda la familia; no recopilamos a sabiendas datos personales de niños.</li>
        <li>Cuando lo exija la ley o la política de la plataforma, los anuncios personalizados se limitan o desactivan para usuarios infantiles.</li>
        <li>Los padres/tutores deben supervisar el uso y pueden gestionar los ajustes de privacidad/publicidad del dispositivo.</li>
      </ul>

      <h3>7) Tus Opciones</h3>
      <ul>
        <li><strong>Eliminar datos locales</strong>: desinstala la App.</li>
        <li><strong>Preferencias de anuncios</strong>: restablece/limita el IDFA/AAID de anuncios, "Limitar el seguimiento de anuncios" / "Inhabilitar la personalización de anuncios" en los ajustes del dispositivo.</li>
        <li><strong>Consentimiento (EEE/Reino Unido cuando corresponda)</strong>: es posible que se te muestre una solicitud de consentimiento para los anuncios; puedes revisar tus elecciones en la App (si está disponible) o en los ajustes del dispositivo.</li>
      </ul>

      <h3>8) Bases Legales y Tus Derechos</h3>
      <p>
        Cuando se aplica el RGPD, nos basamos en: ejecución de un contrato (proporcionar la App), intereses legítimos
        (prevención de fraude, análisis, mejora del servicio) y consentimiento (anuncios personalizados cuando sea necesario).
        Según la KVKK y/o el RGPD (si corresponde), puedes tener derechos de acceso, rectificación, supresión, limitación,
        oposición al procesamiento y portabilidad de datos. Puedes ejercer tus derechos a través del contacto que aparece a continuación;
        para compras/reembolsos, gestiónalos a través de la tienda.
      </p>

      <h3>9) Intercambio de Datos</h3>
      <ul>
        <li>No vendemos tus datos personales.</li>
        <li>Solo compartimos datos con los proveedores de servicios mencionados anteriormente, cuando lo exija la ley o para proteger nuestros derechos/usuarios.</li>
      </ul>

      <h3>10) Transferencias Internacionales</h3>
      <p>
        Los procesadores de terceros (p. ej., AdMob, RevenueCat, Apple/Google) pueden procesar datos en servidores fuera de tu país.
        Cuando sea necesario, pueden aplicarse salvaguardas (como cláusulas contractuales estándar) según sus políticas.
      </p>

      <h3>11) Seguridad</h3>
      <p>
        Usamos medidas técnicas y organizativas razonables y adecuadas a la naturaleza de los datos y de nuestra App.
        Ningún método de transmisión o almacenamiento es 100 % seguro.
      </p>

      <h3>12) Actualizaciones de esta Política</h3>
      <ul>
        <li>Podemos actualizar esta Política de vez en cuando para reflejar cambios en las funciones o en la ley.</li>
        <li>Los cambios importantes se comunicarán en la App y/o a través de la ficha de la tienda. El uso continuado significa que aceptas la Política actualizada.</li>
      </ul>

      <h3>13) Contacto</h3>
      <p>Para preguntas sobre privacidad o para ejercer tus derechos, contáctanos:</p>
      <ul>
        <li>Correo electrónico: <a href="mailto:sozdemircim@gmail.com">sozdemircim@gmail.com</a></li>
        <li>Sitio web: <a href="https://www.suleymanozdemirci.com/project" target="_blank" rel="noreferrer">https://www.suleymanozdemirci.com/project</a></li>
        <li>Procuramos responder en un plazo de 7 días hábiles.</li>
      </ul>

      <h3>14) Declaración de Cumplimiento</h3>
      <ul>
        <li>Diseñada para alinearse con la KVKK de Türkiye.</li>
        <li>Destinada a cumplir las expectativas de divulgación de Google Play / Apple App Store.</li>
        <li>Cuando corresponda, alineada con los requisitos del RGPD/EEE sobre privacidad infantil y publicidad.</li>
      </ul>

      <hr style={{ margin: "30px 0" }} />
      <p><strong>Última actualización: Octubre de 2025</strong></p>
    </>
  );
}

// Language switcher shown above the Hangman Go policy (app ships in tr/en/de/es).
const langSwitchRow = {
  display: "flex",
  gap: "8px",
  justifyContent: "center",
  flexWrap: "wrap",
  marginBottom: "28px",
};

function HangmanLangSwitcher({ lang, setLang }) {
  const langs = [
    ["tr", "Türkçe"],
    ["en", "English"],
    ["de", "Deutsch"],
    ["es", "Español"],
  ];
  return (
    <div style={langSwitchRow}>
      {langs.map(([code, label]) => (
        <button
          key={code}
          type="button"
          onClick={() => setLang(code)}
          style={{
            padding: "6px 16px",
            borderRadius: "20px",
            cursor: "pointer",
            border: lang === code ? "2px solid #2c3e50" : "1px solid #ccc",
            background: lang === code ? "#2c3e50" : "#fff",
            color: lang === code ? "#fff" : "#333",
            fontWeight: 600,
            fontSize: "14px",
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function HangmanGoPrivacy() {
  const [lang, setLang] = React.useState(() => {
    const n = (typeof navigator !== "undefined" ? navigator.language : "en")
      .slice(0, 2)
      .toLowerCase();
    return ["tr", "en", "de", "es"].includes(n) ? n : "en";
  });
  const byLang = {
    tr: HangmanGoPrivacyTR,
    en: HangmanGoPrivacyEN,
    de: HangmanGoPrivacyDE,
    es: HangmanGoPrivacyES,
  };
  const Content = byLang[lang] || HangmanGoPrivacyEN;
  return (
    <div className="legal-content" style={legalStyle}>
      <HangmanLangSwitcher lang={lang} setLang={setLang} />
      <Content />
    </div>
  );
}

function DotsplosionPrivacy() {
  return (
    <div className="legal-content" style={legalStyle}>
      <h1 style={titleStyle}>Privacy Policy for Dotsplosion</h1>

      <p><strong>Effective Date: March 2026</strong></p>

      <h3>1) Scope</h3>
      <p>
        This Privacy Policy explains how Dotsplosion ("App", "we", "us") processes information when you use the App.
        The App is a multiplayer chain-reaction game available on iOS and Android, featuring online matchmaking, friend rooms, and ranked play.
      </p>

      <h3>2) Information We Process</h3>
      <h4>A. Information You Provide</h4>
      <ul>
        <li>No separate account registration is required. Authentication is handled via your Apple ID or Google account through the platform.</li>
        <li>In-App purchase actions are handled by Apple App Store or Google Play; we do not receive your full payment details.</li>
      </ul>

      <h4>B. Automatically Processed Information</h4>
      <ul>
        <li><strong>Local game data</strong>: progress, settings, powerup inventory (stored on your device).</li>
        <li><strong>Online game data</strong>: match results, ELO rating, leaderboard position, and friend room activity (processed on our servers to enable multiplayer features).</li>
        <li><strong>Device & usage data</strong>: basic diagnostics, app version, performance data (used to improve stability and fix bugs).</li>
        <li><strong>Advertising identifiers</strong>: Apple IDFA / Google AAID and related signals processed by ad networks (e.g., Google AdMob) for ad delivery and, where permitted, personalization/frequency capping/fraud prevention.</li>
      </ul>

      <h3>3) Purposes of Processing</h3>
      <ul>
        <li>Provide core gameplay, online matchmaking, ranked ladder, and friend room features.</li>
        <li>Store match results and ELO ratings to enable competitive play and leaderboards.</li>
        <li>Process payments through platform stores.</li>
        <li>Show ads (including rewarded ads where available) and measure their performance.</li>
        <li>Maintain security, prevent cheating/fraud, and improve App performance.</li>
      </ul>

      <h3>4) Data Storage & Retention</h3>
      <ul>
        <li>Local game data is stored on your device. Deleting the App removes local data.</li>
        <li>Online data (match history, ELO rating, leaderboard position) is stored on our servers for as long as necessary to provide the service. You may request deletion via the contact below.</li>
        <li>Purchase records and limited analytics/diagnostic data may be retained as required for legitimate purposes.</li>
      </ul>

      <h3>5) Third-Party Services</h3>
      <p>We integrate third parties to operate the App. Their processing is governed by their own terms/policies:</p>
      <ul>
        <li><strong>Apple App Store / Google Play Billing</strong>: purchases, refunds, price changes.</li>
        <li><strong>Google AdMob</strong>: ad serving, measurement, fraud prevention; may use device identifiers and related signals.</li>
        <li><strong>Firebase</strong>: backend services including real-time matchmaking and data synchronization.</li>
      </ul>

      <h3>6) Children's Privacy</h3>
      <p>
        The App may be used by children under the age of 13. We take children's privacy seriously
        and comply with the Children's Online Privacy Protection Act (COPPA), GDPR provisions for minors,
        and Türkiye's KVKK.
      </p>
      <ul>
        <li>We do <strong>not</strong> knowingly collect, store, or process personal information from children under 13 (or the applicable age of consent in your jurisdiction).</li>
        <li>The App does not require account creation, profile information, or any personal data entry from users of any age.</li>
        <li>Online features (matchmaking, leaderboards) use anonymous or platform-generated identifiers only; no child-identifiable information is collected by us.</li>
        <li>Personalized advertising is <strong>disabled</strong> for all users identified as children by the platform. Only contextual or limited ads are shown where permitted.</li>
        <li>We do not share any child's data with third parties for marketing or promotional purposes.</li>
        <li>If you are a parent or guardian and believe your child has provided personal information through the App, please contact us at <a href="mailto:sozdemircim@gmail.com">sozdemircim@gmail.com</a> so we can promptly delete such information.</li>
        <li>Parents and guardians are encouraged to supervise their children's use of the App and can manage privacy, ad tracking, and in-app purchase settings through their device's parental controls.</li>
      </ul>

      <h3>7) Your Choices</h3>
      <ul>
        <li><strong>Delete local data</strong>: uninstall the App.</li>
        <li><strong>Delete online data</strong>: contact us via email to request deletion of your match history and rating data.</li>
        <li><strong>Ads preferences</strong>: reset/limit Ad IDFA/AAID, "Limit Ad Tracking" / "Opt-out of Ads Personalization" in device settings.</li>
        <li><strong>Consent (EEA/UK where applicable)</strong>: you may be shown a consent prompt for ads; you can revisit choices in the App (if provided) or device settings.</li>
      </ul>

      <h3>8) Legal Bases & Your Rights</h3>
      <p>
        Where the GDPR applies, we rely on: performance of a contract (providing the App and multiplayer services), legitimate interests
        (fraud/cheat prevention, analytics, service improvement), and consent (personalized ads where required).
        Under KVKK and/or GDPR (if applicable), you may have rights to access, correct, delete, restrict, object to processing,
        and data portability. You can exercise rights via the contact below.
      </p>

      <h3>9) Data Sharing</h3>
      <ul>
        <li>We do not sell your personal data.</li>
        <li>Your display name, ELO rating, and match results may be visible to other players on leaderboards and in match lobbies.</li>
        <li>We share data only with service providers listed above, when required by law, or to protect our rights/users.</li>
      </ul>

      <h3>10) International Transfers</h3>
      <p>
        Third-party processors (e.g., AdMob, Firebase, Apple/Google) may process data on servers outside your country.
        Where required, safeguards (such as standard contractual clauses) may apply under their policies.
      </p>

      <h3>11) Security</h3>
      <p>
        We use reasonable technical and organizational measures appropriate to the nature of the data and our App.
        No method of transmission or storage is 100% secure.
      </p>

      <h3>12) Updates to This Policy</h3>
      <ul>
        <li>We may update this Policy from time to time to reflect changes in features or law.</li>
        <li>Material changes will be communicated in-App and/or via the store listing. Continued use means you accept the updated Policy.</li>
      </ul>

      <h3>13) Contact</h3>
      <p>For privacy questions or to exercise your rights, contact us:</p>
      <ul>
        <li>Email: <a href="mailto:sozdemircim@gmail.com">sozdemircim@gmail.com</a></li>
        <li>Website: <a href="https://www.suleymanozdemirci.com/project" target="_blank" rel="noreferrer">https://www.suleymanozdemirci.com/project</a></li>
        <li>We aim to respond within 7 business days.</li>
      </ul>

      <h3>14) Compliance Statement</h3>
      <ul>
        <li>Designed to align with Türkiye's KVKK.</li>
        <li>Intended to meet Google Play / Apple App Store disclosure expectations.</li>
        <li>Where applicable, aligned with GDPR/EEA children's privacy and advertising requirements.</li>
      </ul>

      <hr style={{ margin: "30px 0" }} />
      <p><strong>Last Updated: March 2026</strong></p>
    </div>
  );
}

function WordPopPrivacy() {
  return (
    <div className="legal-content" style={legalStyle}>
      <h1 style={titleStyle}>Privacy Policy for WordPop Blast</h1>

      <p><strong>Effective Date: April 2025</strong></p>

      <h3>1) Scope</h3>
      <p>
        This Privacy Policy explains how WordPop Blast ("App", "we", "us") processes information when you use the App.
        The App is a word puzzle game available on iOS, featuring Game Center leaderboards.
      </p>

      <h3>2) Information We Process</h3>
      <h4>A. Information You Provide</h4>
      <ul>
        <li>No account or personal registration is required to play.</li>
        <li>In-App purchase actions are handled by Apple App Store; we do not receive your full payment details.</li>
      </ul>

      <h4>B. Automatically Processed Information</h4>
      <ul>
        <li><strong>Local game data</strong>: progress, settings, level completion (stored on your device).</li>
        <li><strong>Game Center data</strong>: your Game Center display name and scores may be shared with Apple Game Center for leaderboard functionality. This data is managed by Apple.</li>
        <li><strong>Device & usage data</strong>: basic diagnostics, app version, performance data (used to improve stability and fix bugs).</li>
        <li><strong>Advertising identifiers</strong>: Apple IDFA and related signals processed by ad networks (e.g., Google AdMob) for ad delivery and, where permitted, personalization/frequency capping/fraud prevention.</li>
      </ul>

      <h3>3) Purposes of Processing</h3>
      <ul>
        <li>Provide core gameplay, save local progress, and manage Game Center leaderboards.</li>
        <li>Process payments through Apple App Store.</li>
        <li>Show ads (including rewarded ads where available) and measure their performance.</li>
        <li>Maintain security, prevent fraud/abuse, and improve App performance.</li>
      </ul>

      <h3>4) Data Storage & Retention</h3>
      <ul>
        <li>Game progress is stored locally on your device. Deleting the App removes local data.</li>
        <li>Game Center data is managed by Apple according to their retention policies.</li>
        <li>Limited analytics/diagnostic data may be retained as required for legitimate purposes.</li>
      </ul>

      <h3>5) Third-Party Services</h3>
      <p>We integrate third parties to operate the App. Their processing is governed by their own terms/policies:</p>
      <ul>
        <li><strong>Apple App Store</strong>: purchases, refunds.</li>
        <li><strong>Apple Game Center</strong>: leaderboards and achievements. Your display name and scores may be visible to other players.</li>
        <li><strong>Google AdMob</strong>: ad serving, measurement, fraud prevention; may use device identifiers and related signals.</li>
        <li><strong>Firebase Analytics</strong>: anonymous usage analytics to improve the App.</li>
      </ul>

      <h3>6) Children's Privacy</h3>
      <ul>
        <li>The App is family-friendly; we do not knowingly collect personal data from children under 13.</li>
        <li>Where required by law or platform policy, personalized ads are limited or disabled for child users.</li>
        <li>Parents/guardians should supervise use and can manage device privacy/ad settings.</li>
      </ul>

      <h3>7) Your Choices</h3>
      <ul>
        <li><strong>Delete local data</strong>: uninstall the App.</li>
        <li><strong>Ads preferences</strong>: reset/limit Ad IDFA, "Limit Ad Tracking" in device settings.</li>
        <li><strong>Game Center</strong>: manage your Game Center profile and visibility in iOS settings.</li>
        <li><strong>Consent (EEA/UK where applicable)</strong>: you may be shown a consent prompt for ads; you can revisit choices in the App (if provided) or device settings.</li>
      </ul>

      <h3>8) Legal Bases & Your Rights</h3>
      <p>
        Where the GDPR applies, we rely on: performance of a contract (providing the App), legitimate interests
        (analytics, service improvement), and consent (personalized ads where required).
        Under KVKK and/or GDPR (if applicable), you may have rights to access, correct, delete, restrict, object to processing,
        and data portability. You can exercise rights via the contact below.
      </p>

      <h3>9) Data Sharing</h3>
      <ul>
        <li>We do not sell your personal data.</li>
        <li>We share data only with service providers listed above, when required by law, or to protect our rights/users.</li>
      </ul>

      <h3>10) International Transfers</h3>
      <p>
        Third-party processors (e.g., AdMob, Firebase, Apple) may process data on servers outside your country.
        Where required, safeguards (such as standard contractual clauses) may apply under their policies.
      </p>

      <h3>11) Security</h3>
      <p>
        We use reasonable technical and organizational measures appropriate to the nature of the data and our App.
        No method of transmission or storage is 100% secure.
      </p>

      <h3>12) Updates to This Policy</h3>
      <ul>
        <li>We may update this Policy from time to time to reflect changes in features or law.</li>
        <li>Material changes will be communicated in-App and/or via the store listing. Continued use means you accept the updated Policy.</li>
      </ul>

      <h3>13) Contact</h3>
      <p>For privacy questions or to exercise your rights, contact us:</p>
      <ul>
        <li>Email: <a href="mailto:sozdemircim@gmail.com">sozdemircim@gmail.com</a></li>
        <li>Website: <a href="https://www.suleymanozdemirci.com/project" target="_blank" rel="noreferrer">https://www.suleymanozdemirci.com/project</a></li>
        <li>We aim to respond within 7 business days.</li>
      </ul>

      <h3>14) Compliance Statement</h3>
      <ul>
        <li>Designed to align with Türkiye's KVKK.</li>
        <li>Intended to meet Apple App Store disclosure expectations.</li>
        <li>Where applicable, aligned with GDPR/EEA children's privacy and advertising requirements.</li>
      </ul>

      <hr style={{ margin: "30px 0" }} />
      <p><strong>Last Updated: March 2026</strong></p>
    </div>
  );
}

function OneToFiftyPrivacy() {
  return (
    <div className="legal-content" style={legalStyle}>
      <h1 style={titleStyle}>Privacy Policy for 1to50 Race</h1>

      <p><strong>Effective Date: April 2025</strong></p>

      <h3>1) Scope</h3>
      <p>
        This Privacy Policy explains how 1to50 Race ("App", "we", "us") processes information when you use the App.
        The App is a simple number-tapping speed game available on iOS. No account creation is required.
      </p>

      <h3>2) Information We Process</h3>
      <h4>A. Information You Provide</h4>
      <ul>
        <li>No account or personal registration is required to play.</li>
      </ul>

      <h4>B. Automatically Processed Information</h4>
      <ul>
        <li><strong>Local game data</strong>: best times and settings (stored on your device).</li>
        <li><strong>Device & usage data</strong>: basic diagnostics, app version, performance data (used to improve stability and fix bugs).</li>
        <li><strong>Advertising identifiers</strong>: Apple IDFA and related signals processed by ad networks (e.g., Google AdMob) for ad delivery and, where permitted, personalization/frequency capping/fraud prevention.</li>
      </ul>

      <h3>3) Purposes of Processing</h3>
      <ul>
        <li>Provide core gameplay and save local progress.</li>
        <li>Show ads and measure their performance.</li>
        <li>Maintain security and improve App performance.</li>
      </ul>

      <h3>4) Data Storage & Retention</h3>
      <ul>
        <li>Game progress is stored locally on your device. Deleting the App removes local data.</li>
        <li>Limited analytics/diagnostic data may be retained as required for legitimate purposes.</li>
      </ul>

      <h3>5) Third-Party Services</h3>
      <p>We integrate third parties to operate the App. Their processing is governed by their own terms/policies:</p>
      <ul>
        <li><strong>Apple App Store</strong>: app distribution.</li>
        <li><strong>Google AdMob</strong>: ad serving, measurement, fraud prevention; may use device identifiers and related signals.</li>
      </ul>

      <h3>6) Children's Privacy</h3>
      <ul>
        <li>The App is family-friendly; we do not knowingly collect personal data from children under 13.</li>
        <li>Where required by law or platform policy, personalized ads are limited or disabled for child users.</li>
        <li>Parents/guardians should supervise use and can manage device privacy/ad settings.</li>
      </ul>

      <h3>7) Your Choices</h3>
      <ul>
        <li><strong>Delete local data</strong>: uninstall the App.</li>
        <li><strong>Ads preferences</strong>: reset/limit Ad IDFA, "Limit Ad Tracking" in device settings.</li>
        <li><strong>Consent (EEA/UK where applicable)</strong>: you may be shown a consent prompt for ads; you can revisit choices in the App (if provided) or device settings.</li>
      </ul>

      <h3>8) Legal Bases & Your Rights</h3>
      <p>
        Where the GDPR applies, we rely on: performance of a contract (providing the App), legitimate interests
        (analytics, service improvement), and consent (personalized ads where required).
        Under KVKK and/or GDPR (if applicable), you may have rights to access, correct, delete, restrict, object to processing,
        and data portability. You can exercise rights via the contact below.
      </p>

      <h3>9) Data Sharing</h3>
      <ul>
        <li>We do not sell your personal data.</li>
        <li>We share data only with service providers listed above, when required by law, or to protect our rights/users.</li>
      </ul>

      <h3>10) International Transfers</h3>
      <p>
        Third-party processors (e.g., AdMob, Apple) may process data on servers outside your country.
        Where required, safeguards (such as standard contractual clauses) may apply under their policies.
      </p>

      <h3>11) Security</h3>
      <p>
        We use reasonable technical and organizational measures appropriate to the nature of the data and our App.
        No method of transmission or storage is 100% secure.
      </p>

      <h3>12) Updates to This Policy</h3>
      <ul>
        <li>We may update this Policy from time to time to reflect changes in features or law.</li>
        <li>Material changes will be communicated in-App and/or via the store listing. Continued use means you accept the updated Policy.</li>
      </ul>

      <h3>13) Contact</h3>
      <p>For privacy questions or to exercise your rights, contact us:</p>
      <ul>
        <li>Email: <a href="mailto:sozdemircim@gmail.com">sozdemircim@gmail.com</a></li>
        <li>Website: <a href="https://www.suleymanozdemirci.com/project" target="_blank" rel="noreferrer">https://www.suleymanozdemirci.com/project</a></li>
        <li>We aim to respond within 7 business days.</li>
      </ul>

      <h3>14) Compliance Statement</h3>
      <ul>
        <li>Designed to align with Türkiye's KVKK.</li>
        <li>Intended to meet Apple App Store disclosure expectations.</li>
        <li>Where applicable, aligned with GDPR/EEA children's privacy and advertising requirements.</li>
      </ul>

      <hr style={{ margin: "30px 0" }} />
      <p><strong>Last Updated: March 2026</strong></p>
    </div>
  );
}

function PurpleTrapPrivacy() {
  return (
    <div className="legal-content" style={legalStyle}>
      <h1 style={titleStyle}>Privacy Policy for PurpleTrap</h1>

      <p><strong>Effective Date: April 2026</strong></p>

      <h3>1) Scope</h3>
      <p>
        This Privacy Policy explains how PurpleTrap ("App", "we", "us") processes information when you use the App.
        The App is a single-player color-matching puzzle game available on iOS, featuring Game Center leaderboards.
      </p>

      <h3>2) Information We Process</h3>
      <h4>A. Information You Provide</h4>
      <ul>
        <li>No account or personal registration is required to play.</li>
        <li>In-App purchase actions (such as "Remove Ads") are handled by the Apple App Store; we do not receive your full payment details.</li>
      </ul>

      <h4>B. Automatically Processed Information</h4>
      <ul>
        <li><strong>Local game data</strong>: high score, settings, and ad-removal entitlement (stored on your device).</li>
        <li><strong>Game Center data</strong>: your Game Center display name and scores may be shared with Apple Game Center for leaderboard functionality. This data is managed by Apple.</li>
        <li><strong>Device & usage data</strong>: basic diagnostics, app version, performance data (used to improve stability and fix bugs).</li>
        <li><strong>Advertising identifiers</strong>: Apple IDFA and related signals processed by ad networks (e.g., Google AdMob) for ad delivery and, where permitted, personalization/frequency capping/fraud prevention. Ads are disabled for users who have purchased "Remove Ads".</li>
      </ul>

      <h3>3) Purposes of Processing</h3>
      <ul>
        <li>Provide core gameplay, save local progress, and manage Game Center leaderboards.</li>
        <li>Process payments (e.g., "Remove Ads" IAP) through Apple App Store.</li>
        <li>Show ads (including rewarded ads where available) and measure their performance.</li>
        <li>Maintain security, prevent fraud/abuse, and improve App performance.</li>
      </ul>

      <h3>4) Data Storage & Retention</h3>
      <ul>
        <li>Game progress is stored locally on your device. Deleting the App removes local data.</li>
        <li>Game Center data is managed by Apple according to their retention policies.</li>
        <li>Purchase records and limited analytics/diagnostic data may be retained as required for legitimate purposes.</li>
      </ul>

      <h3>5) Third-Party Services</h3>
      <p>We integrate third parties to operate the App. Their processing is governed by their own terms/policies:</p>
      <ul>
        <li><strong>Apple App Store</strong>: purchases, refunds.</li>
        <li><strong>Apple Game Center</strong>: leaderboards. Your display name and scores may be visible to other players.</li>
        <li><strong>Google AdMob</strong>: ad serving, measurement, fraud prevention; may use device identifiers and related signals.</li>
      </ul>

      <h3>6) Children's Privacy</h3>
      <ul>
        <li>The App is family-friendly; we do not knowingly collect personal data from children under 13.</li>
        <li>Where required by law or platform policy, personalized ads are limited or disabled for child users.</li>
        <li>Parents/guardians should supervise use and can manage device privacy/ad settings.</li>
      </ul>

      <h3>7) Your Choices</h3>
      <ul>
        <li><strong>Delete local data</strong>: uninstall the App.</li>
        <li><strong>Ads preferences</strong>: reset/limit Ad IDFA, "Limit Ad Tracking" in device settings, or purchase "Remove Ads" to disable ads entirely.</li>
        <li><strong>Game Center</strong>: manage your Game Center profile and visibility in iOS settings.</li>
        <li><strong>Consent (EEA/UK where applicable)</strong>: you may be shown a consent prompt for ads; you can revisit choices in the App (if provided) or device settings.</li>
      </ul>

      <h3>8) Legal Bases & Your Rights</h3>
      <p>
        Where the GDPR applies, we rely on: performance of a contract (providing the App), legitimate interests
        (analytics, service improvement), and consent (personalized ads where required).
        Under KVKK and/or GDPR (if applicable), you may have rights to access, correct, delete, restrict, object to processing,
        and data portability. You can exercise rights via the contact below.
      </p>

      <h3>9) Data Sharing</h3>
      <ul>
        <li>We do not sell your personal data.</li>
        <li>Your Game Center display name and score may be visible to other players on leaderboards.</li>
        <li>We share data only with service providers listed above, when required by law, or to protect our rights/users.</li>
      </ul>

      <h3>10) International Transfers</h3>
      <p>
        Third-party processors (e.g., AdMob, Apple) may process data on servers outside your country.
        Where required, safeguards (such as standard contractual clauses) may apply under their policies.
      </p>

      <h3>11) Security</h3>
      <p>
        We use reasonable technical and organizational measures appropriate to the nature of the data and our App.
        No method of transmission or storage is 100% secure.
      </p>

      <h3>12) Updates to This Policy</h3>
      <ul>
        <li>We may update this Policy from time to time to reflect changes in features or law.</li>
        <li>Material changes will be communicated in-App and/or via the store listing. Continued use means you accept the updated Policy.</li>
      </ul>

      <h3>13) Contact</h3>
      <p>For privacy questions or to exercise your rights, contact us:</p>
      <ul>
        <li>Email: <a href="mailto:sozdemircim@gmail.com">sozdemircim@gmail.com</a></li>
        <li>Website: <a href="https://www.suleymanozdemirci.com/project" target="_blank" rel="noreferrer">https://www.suleymanozdemirci.com/project</a></li>
        <li>We aim to respond within 7 business days.</li>
      </ul>

      <h3>14) Compliance Statement</h3>
      <ul>
        <li>Designed to align with Türkiye's KVKK.</li>
        <li>Intended to meet Apple App Store disclosure expectations.</li>
        <li>Where applicable, aligned with GDPR/EEA children's privacy and advertising requirements.</li>
      </ul>

      <hr style={{ margin: "30px 0" }} />
      <p><strong>Last Updated: April 2026</strong></p>
    </div>
  );
}

function HootshotPrivacy() {
  return (
    <div className="legal-content" style={legalStyle}>
      <h1 style={titleStyle}>Privacy Policy for HootShot</h1>

      <p><strong>Effective Date: April 2026</strong></p>

      <h3>1) Scope</h3>
      <p>
        This Privacy Policy explains how HootShot ("App", "we", "us") processes information when you use the App.
        HootShot is a menu-bar screenshot, annotation, and image utility for macOS. It is designed to be
        used without an account or sign-in.
      </p>

      <h3>2) Information We Process</h3>
      <h4>A. Information You Provide</h4>
      <ul>
        <li>No account or personal registration is required to use the App.</li>
        <li>In-App purchase actions, if any, are handled by the Apple App Store; we do not receive your full payment details.</li>
      </ul>

      <h4>B. Automatically Processed Information</h4>
      <ul>
        <li><strong>Local app data</strong>: your preferences, language, hotkey configuration, capture history references, and feature settings (stored on your device).</li>
        <li><strong>Captured images & extracted text</strong>: screenshots, annotations, picked colors, and OCR text are processed and stored only on your device. They are not transmitted to our servers.</li>
        <li><strong>System permissions</strong>: macOS may prompt you to grant Screen Recording, Accessibility, or Files access permissions. These are required for capture, hotkey, and export features and are managed by macOS.</li>
        <li><strong>Device & usage data</strong>: basic diagnostics, app version, and performance data (used to improve stability and fix bugs).</li>
      </ul>

      <h3>3) Purposes of Processing</h3>
      <ul>
        <li>Provide core capture, annotation, OCR, color picking, resize, and export features.</li>
        <li>Save your preferences and recent settings locally.</li>
        <li>Process payments through Apple App Store, where applicable.</li>
        <li>Maintain security, prevent abuse, and improve App performance.</li>
      </ul>

      <h3>4) Data Storage & Retention</h3>
      <ul>
        <li>All captures, annotations, OCR results, and settings are stored locally on your device. Deleting the App removes local data.</li>
        <li>The App does not maintain user accounts or cloud-stored screenshot history.</li>
        <li>Purchase records and limited diagnostic data may be retained as required for legitimate purposes (e.g., billing, debugging).</li>
      </ul>

      <h3>5) Third-Party Services</h3>
      <p>The App relies on the following first/third-party services. Their processing is governed by their own terms/policies:</p>
      <ul>
        <li><strong>Apple App Store</strong>: app distribution, purchases, and refunds.</li>
        <li><strong>Apple Vision framework</strong>: on-device OCR (text extraction). Image data is processed locally on your Mac and is not sent to Apple servers.</li>
      </ul>

      <h3>6) Children's Privacy</h3>
      <ul>
        <li>The App is a general-purpose productivity utility and is not directed to children under 13.</li>
        <li>We do not knowingly collect personal information from children.</li>
      </ul>

      <h3>7) Your Choices</h3>
      <ul>
        <li><strong>Delete local data</strong>: uninstall the App, or delete its data via macOS &gt; Privacy &amp; Security and the user Library folder.</li>
        <li><strong>Permissions</strong>: revoke Screen Recording, Accessibility, or Files access at any time via System Settings &gt; Privacy &amp; Security.</li>
        <li><strong>Captures</strong>: you fully control what is captured, where it is saved, and when it is deleted.</li>
      </ul>

      <h3>8) Legal Bases & Your Rights</h3>
      <p>
        Where the GDPR applies, we rely on: performance of a contract (providing the App), and legitimate interests
        (analytics, service improvement). Under KVKK and/or GDPR (if applicable), you may have rights to access, correct,
        delete, restrict, object to processing, and data portability. You can exercise rights via the contact below.
      </p>

      <h3>9) Data Sharing</h3>
      <ul>
        <li>We do not sell your personal data.</li>
        <li>We do not transmit your screenshots, annotations, or OCR results off your device.</li>
        <li>We share data only with service providers listed above, when required by law, or to protect our rights/users.</li>
      </ul>

      <h3>10) International Transfers</h3>
      <p>
        Where any third-party processor (e.g., Apple) processes data on servers outside your country, safeguards
        (such as standard contractual clauses) may apply under their policies.
      </p>

      <h3>11) Security</h3>
      <p>
        We use reasonable technical and organizational measures appropriate to the nature of the data and our App.
        Captures stay on your device unless you choose to share or export them. No method of transmission or storage is 100% secure.
      </p>

      <h3>12) Updates to This Policy</h3>
      <ul>
        <li>We may update this Policy from time to time to reflect changes in features or law.</li>
        <li>Material changes will be communicated in-App and/or via the store listing. Continued use means you accept the updated Policy.</li>
      </ul>

      <h3>13) Contact</h3>
      <p>For privacy questions or to exercise your rights, contact us:</p>
      <ul>
        <li>Email: <a href="mailto:sozdemircim@gmail.com">sozdemircim@gmail.com</a></li>
        <li>Website: <a href="https://www.suleymanozdemirci.com/project" target="_blank" rel="noreferrer">https://www.suleymanozdemirci.com/project</a></li>
        <li>We aim to respond within 7 business days.</li>
      </ul>

      <h3>14) Compliance Statement</h3>
      <ul>
        <li>Designed to align with Türkiye's KVKK.</li>
        <li>Intended to meet Apple Mac App Store disclosure expectations.</li>
        <li>Where applicable, aligned with GDPR/EEA privacy requirements.</li>
      </ul>

      <hr style={{ margin: "30px 0" }} />
      <p><strong>Last Updated: April 2026</strong></p>
    </div>
  );
}

const privacyComponents = {
  hangmango: HangmanGoPrivacy,
  dotsplosion: DotsplosionPrivacy,
  wordpop: WordPopPrivacy,
  "1to50": OneToFiftyPrivacy,
  purpletrap: PurpleTrapPrivacy,
  hootshot: HootshotPrivacy,
};

function PrivacyPolicy() {
  const { game } = useParams();

  const ContentComponent = game ? privacyComponents[game] : null;

  if (!ContentComponent) {
    return <Navigate to="/project" />;
  }

  return (
    <Container fluid className="about-section">
      <Particle />
      <Container>
        <ContentComponent />
      </Container>
    </Container>
  );
}

export default PrivacyPolicy;
